// Trading Monitor (2026-10-08, T-195): a page of its own (not a tab) for the Tier 2 fault monitor. The worker is scripts/trading_monitor_worker.py (tokenless,
// reads counts from the logging tables); this page switches it on and off, picks the interval, runs it now, and shows the faults it found.
import React, { useState, useEffect, useCallback } from 'react'
import { Activity, Play, Loader2, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react'
import { tradingMonitor } from '../lib/api'
import { timeAgo } from '../lib/utils'

const MONO = { fontFamily: 'JetBrains Mono, monospace' }
const INTERVAL_LABELS = { 5: '5 minutes', 30: '30 minutes', 60: '60 minutes', 120: '2 hours', 1440: '1 day' }
const SEV = { critical: 'text-rose-400', high: 'text-amber-400', medium: 'text-sky-400', low: 'text-slate-400' }

export default function TradingMonitor() {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState(null)

  const load = useCallback(async () => {
    try {
      const r = await tradingMonitor.state()
      if (!r.success) throw new Error(r.error || 'failed')
      setData(r)
      setError(null)
    } catch (e) {
      setError(e.message || String(e))
    }
  }, [])

  useEffect(() => {
    load()
    const id = setInterval(load, 15_000)
    return () => clearInterval(id)
  }, [load])

  const save = async (enabled, interval_min) => {
    setBusy(true)
    setNote(null)
    try {
      const r = await tradingMonitor.setConfig(enabled, interval_min)
      if (!r.success) throw new Error(r.error || 'failed')
      await load()
    } catch (e) {
      setNote(e.message || String(e))
    } finally {
      setBusy(false)
    }
  }

  const runNow = async () => {
    setBusy(true)
    setNote(null)
    try {
      const r = await tradingMonitor.runNow()
      setNote(r.success ? 'Run started; the result appears below within a few seconds.' : (r.error || 'failed'))
      setTimeout(load, 4000)
    } catch (e) {
      setNote(e.message || String(e))
    } finally {
      setBusy(false)
    }
  }

  const cfg = data?.config
  const st = data?.state || {}
  const faults = st.faults || []

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-slate-200 font-semibold text-sm tracking-widest flex items-center gap-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
          <Activity className="h-4 w-4 text-cyan-400" />
          TRADING MONITOR
        </h2>
        <p className="text-slate-500 text-xs mt-0.5">
          Tier 2 fault watch. A scheduled script counts quote blocks, order failures, cycle gaps and open alerts from the logging tables (no AI calls) and stops
          checking when the market closes. MAIN is not covered yet.
        </p>
      </div>

      {error && <p className="text-rose-400 text-xs break-words">Could not load the monitor: {error}</p>}

      {cfg && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <button
              role="switch" aria-checked={cfg.enabled} disabled={busy}
              onClick={() => save(!cfg.enabled, cfg.interval_min)}
              className="flex items-center gap-2 text-xs text-slate-300">
              <span className="relative inline-block w-10 h-5 rounded-full transition-colors" style={{ background: cfg.enabled ? '#10b981' : '#334155' }}>
                <span className="absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all" style={{ left: cfg.enabled ? 22 : 2 }} />
              </span>
              <span style={MONO}>{cfg.enabled ? 'ENABLED' : 'DISABLED'}</span>
            </button>

            <label className="text-slate-400 text-xs flex items-center gap-2">
              Check every
              <select
                value={cfg.interval_min} disabled={busy}
                onChange={e => save(cfg.enabled, Number(e.target.value))}
                className="bg-slate-900 border border-slate-800 rounded px-2 py-1 text-slate-300 text-xs" style={MONO}>
                {(data.interval_choices_min || []).map(m => <option key={m} value={m}>{INTERVAL_LABELS[m] || `${m} min`}</option>)}
              </select>
            </label>

            <button onClick={runNow} disabled={busy || data.running}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-cyan-500/90 text-slate-950 hover:bg-cyan-400 disabled:opacity-50 flex items-center gap-1.5">
              {data.running ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}Run now
            </button>
            <button onClick={load} className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1"><RefreshCw className="h-3 w-3" />Refresh</button>
          </div>
          {note && <p className="text-slate-400 text-xs break-words">{note}</p>}

          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div><dt className="text-slate-500 text-[10px] tracking-widest">LAST RUN</dt><dd style={MONO} className="text-slate-200">{st.last_run_at ? timeAgo(st.last_run_at) : 'never'}</dd></div>
            <div><dt className="text-slate-500 text-[10px] tracking-widest">MARKET</dt><dd style={MONO} className="text-slate-200">{st.market_open === undefined ? 'unknown' : (st.market_open ? 'open' : 'closed')}</dd></div>
            <div><dt className="text-slate-500 text-[10px] tracking-widest">SCHEDULE</dt><dd style={MONO} className="text-slate-200">{st.suspended_reason || (cfg.enabled ? 'active' : 'off')}</dd></div>
            <div><dt className="text-slate-500 text-[10px] tracking-widest">QUEUED CHOICES</dt><dd style={MONO} className="text-slate-200">{data.queue_pending}</dd></div>
          </dl>
          {(st.blind || []).length > 0 && <p className="text-amber-400 text-xs">Checks that could not run: {st.blind.join(', ')}</p>}
        </div>
      )}

      <div className="space-y-2">
        <p className="text-slate-500 text-[10px] tracking-widest">FAULTS</p>
        {faults.length === 0 && (
          <p className="text-slate-400 text-sm flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-400" />
            {st.last_run_at ? 'No Tier 2 fault at the last run.' : 'No run recorded yet.'}
          </p>
        )}
        {faults.map(f => (
          <div key={f.id} className="rounded-xl border border-slate-800 bg-slate-900/60 p-3 space-y-1">
            <p className={`text-sm font-semibold flex items-center gap-2 ${SEV[f.severity] || SEV.low}`} style={MONO}>
              <AlertTriangle className="h-3.5 w-3.5" />{f.kind} <span className="text-[10px] font-normal text-slate-500">{f.severity}</span>
            </p>
            <p className="text-slate-300 text-xs">{f.summary}</p>
            <p className="text-slate-500 text-[11px]">{f.runbook}</p>
            <p className="text-slate-600 text-[10px]" style={MONO}>
              {f.choice ? `answered: ${f.choice.action === 'fix_now' ? 'FIX NOW (queued)' : 'ADD TO TODO DOC (queued)'}` : 'awaiting your choice'}
            </p>
            {!f.choice && (
              <div className="flex gap-2 pt-1">
                <button onClick={async () => { await tradingMonitor.choose(f.id, 'fix_now'); load() }} className="text-[11px] px-2 py-1 rounded bg-slate-800 text-slate-200 hover:bg-slate-700">[ FIX NOW ]</button>
                <button onClick={async () => { await tradingMonitor.choose(f.id, 'add_todo'); load() }} className="text-[11px] px-2 py-1 rounded bg-slate-800 text-slate-200 hover:bg-slate-700">[ ADD TO TODO DOC ]</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
